/** @jsxImportSource @emotion/react */

import { useTheme } from '@emotion/react';
import { ChangeEvent, MouseEvent, useState } from 'react';
import slugify from 'slugify';

import { ReactComponent as Checkmark } from './checkmark.svg';
import { ReactComponent as ChevronDown } from './chevron-down.svg';
import { ReactComponent as ChevronUp } from './chevron-up.svg';
import { Answer } from '../Data/index';

type answerProps = {
  answer: Answer;
  index: number;
  handleChange: (e: ChangeEvent<HTMLInputElement>, question: string) => void;
  selectedValue: string | null;
  question: string;
  questionIndex: number;
  questionWeight: number;
};

export const AnswerCard = ({ answer, handleChange, index, question, questionIndex, questionWeight, selectedValue }: answerProps) => {
  const theme = useTheme();
  const id = `question-${questionIndex}-answer-${index}`;
  const [isExpanded, setIsExpanded] = useState(false);

  const isMeme = !answer.text;
  const answerSelected = selectedValue === answer.subject;

  const expandCard = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    setIsExpanded((prevIsExpanded) => !prevIsExpanded);
  };

  return (
    <div
      css={{
        background: theme.color.white,
        boxShadow: '0px 0.25rem 0.5rem rgba(0, 0, 0, 0.15)',
        outline: '2px solid',
        // 195px is height of 2 line title & 2 line attribute
        minHeight: isMeme ? undefined : '195px',
        position: 'relative',
        transition: 'box-shadow 0.25s ease',

        '&:before': {
          background: 'transparent',
          top: '100%',
          content: '""',
          height: '2px',
          left: 0,
          position: 'absolute',
          transition: 'background 150ms ease',
          width: '100%',
          zIndex: 1
        },

        '&:focus, &:focus-within, &:hover': {
          '&:before': {
            background: theme.color.orange
          },

          boxShadow: '0px 0.25rem 0.5rem rgba(0, 0, 0, 0.25)'
        },

        [`@media (min-width: 640px)`]: {
          minHeight: isMeme ? '255px' : '284px'
        }
      }}
      style={{
        outlineColor: answerSelected ? `${theme.color.orange}` : `transparent`
      }}
    >
      <div
        css={{
          position: 'relative',
          height: isMeme ? '219px' : undefined,

          [`@media (min-width: 627px)`]: {
            height: isMeme ? '255px' : undefined
          }
        }}
      >
        <label
          css={{
            cursor: 'pointer'
          }}
        >
          <input
            css={{
              cursor: 'pointer',
              position: 'absolute',
              opacity: 0
            }}
            checked={selectedValue === answer.subject}
            id={`${id}-input`}
            name={slugify(question, { lower: true, strict: true })}
            onChange={(event) => {
              handleChange(event, question);
            }}
            required
            type="radio"
            data-value-weight={questionWeight}
            value={answer.subject}
          />
          <Checkmark
            css={{
              color: theme.color.orange,
              position: 'absolute',
              top: '0.5rem',
              right: '0.5rem',
              height: '1rem',
              width: '1rem',

              [`@media (min-width: 768px)`]: {
                top: '1rem',
                right: '1rem',
                height: '30px',
                width: '30px'
              }
            }}
            style={{
              display: answerSelected ? 'block' : 'none'
            }}
          />
          <picture
            css={{
              display: 'block',
              height: isMeme ? '100%' : undefined
            }}
          >
            <source
              srcSet={`${answer.image.src.replace('.jpg', '@1x.webp')} 322w, ${answer.image.src.replace('.jpg', '@2x.webp')} 644w`}
              sizes="(min-width: 1024px) 322px, calc(50vw - 20px)"
              type="image/webp"
            />
            <source
              srcSet={`${answer.image.src.replace('.jpg', '@1x.jpg')} 322w, ${answer.image.src.replace('.jpg', '@2x.jpg')} 644w`}
              sizes="(min-width: 1024px) 322px, calc(50vw - 20px)"
              type="image/jpeg"
            />
            <img
              alt={answer.image.alt}
              css={{
                display: 'block',
                height: isMeme ? '100%' : 'auto',
                maxWidth: '100%',
                width: '100%',
                maxHeight: isMeme ? '255px' : undefined,
                objectFit: 'contain',
                objectPosition: 'center center'
              }}
              height="192"
              loading="lazy"
              src={answer.image.src.replace('.jpg', '@1x.jpg')}
              width="322"
            />
          </picture>

          {answer.text && (
            <div
              css={{
                background: theme.color.white,
                minHeight: '75px',

                [`@media (min-width: 640px)`]: {
                  minHeight: '92px'
                }
              }}
            >
              <div
                css={{
                  alignItems: 'flex-start',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}
              >
                <div
                  css={{
                    padding: '.75rem 0 .75rem .75rem',

                    [`@media (min-width: 640px)`]: {
                      padding: '1.5rem 0 1.5rem 1.5rem'
                    }
                  }}
                >
                  <p
                    css={{
                      color: theme.color.slateDark,
                      fontSize: theme.fontSizes[2],
                      fontWeight: theme.fontWeights.semibold,
                      margin: 0,

                      [`@media (min-width: 640px)`]: {
                        fontSize: theme.fontSizes[4]
                      }
                    }}
                  >
                    {answer.text}
                  </p>

                  {answer.attribution && (
                    <p
                      css={{
                        color: theme.color.slate,
                        fontSize: theme.fontSizes[1],
                        margin: '4px 0 0 0',

                        [`@media (min-width: 640px)`]: {
                          fontSize: theme.fontSizes[2]
                        }
                      }}
                    >
                      {answer.attribution}
                    </p>
                  )}
                </div>

                {answer.tidbit && (
                  <button
                    aria-controls={`${id}-tidbit`}
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? 'Hide tidbit' : 'Show tidbit'}
                    css={{
                      background: 'transparent',
                      border: 'none',
                      color: theme.color.slateDark,
                      cursor: 'pointer',
                      label: 'tidbit-expand-button',
                      padding: '0.75rem',

                      '&:focus svg, &:hover svg': {
                        fill: theme.color.primary
                      },

                      '&:focus:not(:focus-visible)': {
                        outline: 'none',
                      },

                      [`@media (min-width: 640px)`]: {
                        padding: '1.5rem'
                      }
                    }}
                    onClick={expandCard}
                    type="button"
                  >
                    {isExpanded ? <ChevronUp /> : <ChevronDown />}
                  </button>
                )}
              </div>

              <div
                css={{
                  label: 'tidbit-container',
                  padding: '0 0.75rem 0.75rem 0.75rem',

                  [`@media (min-width: 640px)`]: {
                    padding: '0 1.5rem 1.5rem 1.5rem'
                  }
                }}
                hidden={!isExpanded}
                id={`${id}-tidbit`}
              >
                <p
                  css={{
                    color: theme.color.slateDark,
                    fontSize: theme.fontSizes[1],
                    margin: '0',

                    [`@media (min-width: 640px)`]: {
                      fontSize: theme.fontSizes[2]
                    }
                  }}
                >
                  {answer.tidbit}
                </p>
              </div>
            </div>
          )}
        </label>
      </div>
    </div>
  );
};

AnswerCard.displayName = 'AnswerCard';
